import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AppstoreAddOutlined,
  DownOutlined,
  MinusOutlined,
  PlusOutlined,
  SearchOutlined,
  ShoppingOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Drawer,
  Empty,
  Flex,
  Input,
  Pagination,
  Space,
  Tag,
  Typography,
} from 'antd';

const { Text, Title } = Typography;

const getImageUrl = (variant) => {
  const image = variant?.image || variant?.images?.[0] || variant?.product?.thumbnail || variant?.product?.images?.[0];
  if (!image) return null;
  if (typeof image === 'string') return image;
  return image.sm_url || image.url || null;
};

const PressButton = ({ icon, onPress, ...props }) => {
  const holdTimeoutRef = useRef(null);
  const holdIntervalRef = useRef(null);

  const clearPressTimers = () => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }

    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearPressTimers();
  }, []);

  const startPress = (event) => {
    event.preventDefault();
    event.stopPropagation();

    onPress();

    holdTimeoutRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        onPress();
      }, 120);
    }, 350);
  };

  const stopPress = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    clearPressTimers();
  };

  return (
    <Button
      {...props}
      icon={icon}
      style={{
        touchAction: 'manipulation',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        ...(props.style || {}),
      }}
      onPointerDown={startPress}
      onPointerUp={stopPress}
      onPointerLeave={stopPress}
      onPointerCancel={stopPress}
      onContextMenu={(event) => event.preventDefault()}
    />
  );
};

const QtyControl = ({ variantId, qty, addProduct, onLineQuantityChange, getCurrentQty }) => {
  return (
    <Flex align='center' gap={8} onClick={(event) => event.stopPropagation()} style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
      <PressButton
        size='small'
        shape='circle'
        icon={<MinusOutlined />}
        disabled={qty <= 0}
        onPress={() => {
          const currentQty = getCurrentQty(variantId);
          onLineQuantityChange(variantId, currentQty - 1);
        }}
      />
      <Text style={{ minWidth: 18, textAlign: 'center', fontWeight: 600, userSelect: 'none', WebkitUserSelect: 'none' }}>{qty}</Text>
      <PressButton size='small' shape='circle' type='primary' icon={<PlusOutlined />} onPress={() => addProduct(variantId)} />
    </Flex>
  );
};

const VariantRow = ({ variant, qty, addProduct, onLineQuantityChange, getCurrentQty }) => {
  const variantTitle = variant.variant_title || 'Mặc định';

  return (
    <Flex
      align='center'
      justify='space-between'
      gap={12}
      style={{
        padding: '10px 12px',
        borderRadius: 10,
        border: '1px solid #f0f0f0',
        background: qty > 0 ? '#f6ffed' : '#fff',
      }}
      onClick={() => {
        if (qty === 0) {
          addProduct(variant.id);
        }
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ display: 'block', fontWeight: 500 }} ellipsis={{ tooltip: variantTitle }}>
          {variantTitle}
        </Text>
      </div>

      {qty > 0 ? (
        <QtyControl
          variantId={variant.id}
          qty={qty}
          addProduct={addProduct}
          onLineQuantityChange={onLineQuantityChange}
          getCurrentQty={getCurrentQty}
        />
      ) : (
        <Button type='primary' size='small'>Thêm</Button>
      )}
    </Flex>
  );
};

const VARIANTS_COLLAPSED_COUNT = 3;

const ProductGroupCard = ({ group, qtyByVariantId, addProduct, onLineQuantityChange, getCurrentQty }) => {
  const totalQty = group.variants.reduce((sum, variant) => sum + (qtyByVariantId[variant.id] ?? 0), 0);
  const imageUrl = getImageUrl(group.variants[0]);
  const hasMany = group.variants.length > VARIANTS_COLLAPSED_COUNT;
  const [expanded, setExpanded] = useState(false);

  const visibleVariants = hasMany && !expanded
    ? group.variants.slice(0, VARIANTS_COLLAPSED_COUNT)
    : group.variants;

  return (
    <Card
      size='small'
      styles={{ body: { padding: 12 } }}
      style={{
        borderColor: totalQty > 0 ? '#b7eb8f' : '#f0f0f0',
        boxShadow: totalQty > 0 ? '0 6px 18px rgba(82, 196, 26, 0.12)' : 'none',
      }}
    >
      <Flex gap={12} align='flex-start' style={{ marginBottom: 12 }}>
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 12,
            overflow: 'hidden',
            background: '#fafafa',
            flexShrink: 0,
            border: '1px solid #f0f0f0',
          }}
        >
          {imageUrl ? (
            <img src={imageUrl} alt={group.productName} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <Flex align='center' justify='center' style={{ width: '100%', height: '100%' }}>
              <ShoppingOutlined style={{ fontSize: 24, color: '#d9d9d9' }} />
            </Flex>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex justify='space-between' align='start' gap={8}>
            <Title level={5} style={{ margin: 0, flex: 1 }} ellipsis={{ rows: 2, tooltip: group.productName }}>
              {group.productName}
            </Title>
            {totalQty > 0 && <Tag color='green'>SL {totalQty}</Tag>}
          </Flex>
          <Text type='secondary'>
            {group.variants.length === 1 ? '1 phân loại' : `${group.variants.length} phân loại`}
          </Text>
        </div>
      </Flex>

      <Space direction='vertical' size={8} style={{ width: '100%' }}>
        {visibleVariants.map((variant) => (
          <VariantRow
            key={variant.id}
            variant={variant}
            qty={qtyByVariantId[variant.id] ?? 0}
            addProduct={addProduct}
            onLineQuantityChange={onLineQuantityChange}
            getCurrentQty={getCurrentQty}
          />
        ))}
        {hasMany && (
          <Button
            type='link'
            block
            icon={expanded ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setExpanded((prev) => !prev)}
            style={{ padding: 0, height: 32 }}
          >
            {expanded
              ? 'Thu gọn'
              : `Xem thêm ${group.variants.length - VARIANTS_COLLAPSED_COUNT} phân loại`}
          </Button>
        )}
      </Space>
    </Card>
  );
};

const ProductPickerPanelMobile = ({ viewModel }) => {
  const {
    openProductPicker,
    setOpenProductPicker,
    setProductFilterValue,
    qtyByVariantId,
    addProduct,
    onLineQuantityChange,
    page,
    setPage,
    pagedGroups,
    productGroups,
    getCurrentQty,
    pageSize,
  } = viewModel;

  const [keywordInput, setKeywordInput] = useState('');

  const selectedVariantCount = useMemo(() => {
    return Object.values(qtyByVariantId || {}).filter((qty) => qty > 0).length;
  }, [qtyByVariantId]);

  const selectedQuantity = useMemo(() => {
    return Object.values(qtyByVariantId || {}).reduce((sum, qty) => sum + (qty || 0), 0);
  }, [qtyByVariantId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setProductFilterValue('info', keywordInput);
    }, 200);

    return () => clearTimeout(timeout);
  }, [keywordInput, setProductFilterValue]);

  const onClose = () => {
    setOpenProductPicker(false);
    setKeywordInput('');
    setProductFilterValue('info', '');
    setPage(1);
  };

  const onOpen = () => {
    setOpenProductPicker(true);
  };

  return (
    <>
      <Button type='dashed' block icon={<AppstoreAddOutlined />} onClick={onOpen} style={{ marginTop: 4, height: 44 }}>
        Thêm sản phẩm
        {selectedQuantity > 0 ? ` (${selectedQuantity})` : ''}
      </Button>

      <Drawer
        title='Thêm sản phẩm'
        placement='bottom'
        open={openProductPicker}
        onClose={onClose}
        height='86vh'
        styles={{ body: { padding: 12, paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' } }}
      >
        <Space direction='vertical' size={12} style={{ width: '100%' }}>
          <Card size='small' styles={{ body: { padding: 12 } }}>
            <Space direction='vertical' size={10} style={{ width: '100%' }}>
              <Input
                value={keywordInput}
                onChange={(event) => setKeywordInput(event.target.value)}
                allowClear
                prefix={<SearchOutlined />}
                placeholder='Tìm tên sản phẩm'
              />

              <Flex justify='space-between' gap={8} wrap='wrap'>
                <Text type='secondary'>{productGroups.length} sản phẩm phù hợp</Text>
                <Flex gap={8} wrap='wrap'>
                  <Tag color='blue'>Mặt hàng: {selectedVariantCount}</Tag>
                  <Tag color='green'>Số lượng: {selectedQuantity}</Tag>
                </Flex>
              </Flex>
            </Space>
          </Card>

          {pagedGroups.length > 0 ? (
            <Space direction='vertical' size={12} style={{ width: '100%' }}>
              {pagedGroups.map((group) => (
                <ProductGroupCard
                  key={group.productId}
                  group={group}
                  qtyByVariantId={qtyByVariantId}
                  addProduct={addProduct}
                  onLineQuantityChange={onLineQuantityChange}
                  getCurrentQty={getCurrentQty}
                />
              ))}
            </Space>
          ) : (
            <Card size='small'>
              <Empty description='Không tìm thấy sản phẩm phù hợp' image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Card>
          )}

          <Flex justify='center'>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={productGroups.length}
              onChange={setPage}
              showSizeChanger={false}
              hideOnSinglePage
              size='small'
            />
          </Flex>

          <Button type='primary' block size='large' onClick={onClose}>
            Xong
          </Button>
        </Space>
      </Drawer>
    </>
  );
};

export default ProductPickerPanelMobile;